import React, { useState, useEffect, useRef } from 'react';
import { sortBy } from 'lodash';
import Card from 'components/v2/card';
import CargomaticInput from 'components/v2/input';
import CargomaticSelect from 'components/v2/select';
import AccessorialCard from 'components/modules/tools/AccessorialCard';
import { getCircleButtonClass } from 'components/v2/buttons/CircleButton';
import AccordionSection from 'components/ui/AccordionSectionV2';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import LoadingCircle from 'components/ui/LoadingCircle';
import { createQuote, getDistanceV2 } from 'utils/ShipmentWebAPIUtils';
import { showToast } from 'stores/ToastStore';
import {
  getContainerSizes,
  getContainerTypes,
  getMovementTypes,
  getLocations,
  getPrice,
} from 'utils/DrayageQuotesAPI';
import { getAccessorials } from 'utils/AccessorialWebAPIUtils';
import { metersToMiles } from 'helpers/helper_functions';
import SessionStore from 'stores/SessionStore';
import formatMoney from 'utils/formatters/Money';

const ports = sortBy(getLocations(), (port) => port.name);

const availableAccessorials = ['overweight', 'reefer', 'hazmat'];

function formatMileageBand({ start, end, rpm }) {
  const label = `${start}-${end} Mileage Band`;
  const minRpm = `${formatMoney(rpm)} Rate Per Mile`;
  return (
    <div>
      <div>{label}</div>
      <div>{minRpm}</div>
    </div>
  );
}

function formatAccessorials(accessorials) {
  return accessorials.map((acc) => acc.name).join(', ');
}

const defaultPort = '';
const defaultMovementType = '';
const containerSizes = getContainerSizes();
const containerTypes = getContainerTypes();
const movementTypes = getMovementTypes();
const defaultEquipmenType =
  containerTypes.length === 1 ? containerTypes[0] : '';
const defaultContainerSize =
  containerSizes.length === 1 ? containerTypes[0] : '';

const DrayageQuoteForm = () => {
  const [fetchingDistance, setFetchingDistance] = useState(false);
  const [deliveries, setDeliveries] = useState([
    { address: '', lng: null, lat: null, coords: [], isNew: true },
  ]);
  const [mileageBand, setMileageBand] = useState(undefined);
  const [distance, setDistance] = useState(undefined);
  const [origin, setOrigin] = useState(undefined);
  const [price, setPrice] = useState(undefined);

  const [equipmentType, setEquipmentType] = useState(defaultEquipmenType);

  const [size, setSize] = useState(defaultContainerSize);
  const [movementType, setMovementType] = useState(defaultMovementType);
  const [accessorials, setAccessorials] = useState([]);
  const [payables, setPayables] = useState([]);
  const [receivables, setReceivables] = useState([]);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [requestCompleted, setRequestCompleted] = useState(false);

  const [accessorialsToggled, setAccessorialsToggled] = useState(true);
  const [disclaimerToggled, setDisclaimerToggled] = useState(false);

  const [port, setPort] = useState(defaultPort);

  const [errors, setErrors] = useState({ deliveries: {} });

  const deliveriesRef = useRef(deliveries.length);

  const mountedRef = useRef();
  const newFormRef = useRef(true);

  function shouldValidateForm() {
    return mountedRef.current && !newFormRef.current;
  }

  useEffect(() => {
    if (shouldValidateForm()) {
      setErrors({ ...errors, ...validatePort() });
    }
  }, [port]);

  useEffect(() => {
    if (shouldValidateForm()) {
      setErrors({ ...errors, ...validateDeliveries(true) });
    }
  }, [deliveries]);

  useEffect(() => {
    if (shouldValidateForm()) {
      setErrors({ ...errors, ...validateContainerSize() });
    }
  }, [size]);

  useEffect(() => {
    if (shouldValidateForm()) {
      setErrors({ ...errors, ...validateContainerType() });
    }
  }, [equipmentType]);

  useEffect(() => {
    if (shouldValidateForm()) {
      setErrors({ ...errors, ...validateMovementType() });
    }
  }, [movementType]);

  useEffect(() => {
    loadAccessorials();
    mountedRef.current = true;
  }, []);

  useEffect(() => {
    if (!origin || !deliveries.every((d) => d.address)) {
      setDistance(null);
      return;
    }
    fetchDistance();
  }, [origin, deliveries]);

  useEffect(() => {
    if (!fetchingDistance && distance) {
      getQuotePrice();
    }
  }, [port, distance, accessorials]);

  async function fetchDistance() {
    if (!(deliveries.every((d) => d.address) && origin.address)) {
      return;
    }

    const locations = [origin, ...deliveries];

    const stops = locations.map((loc, i) => {
      const { lat, lng } = loc;
      const location = {
        ...loc,
        coords: [lng, lat],
      };
      return {
        sequence: i + 1,
        location: [location],
      };
    });
    const shipperId = SessionStore.getUser()._id
    try {
      const res = await getDistanceV2({ stops, shipperId });
      setFetchingDistance(false);
      setDistance(res.entity.distance);
    } catch (e) {
      setFetchingDistance(false);
      console.error('Error getting distance:', e);
    }
  }

  function hidePriceIfNewQuote() {
    if (requestCompleted) {
      setSuccess(false);
      setSaving(false);
      setRequestCompleted(false);
    }
  }

  function handleSelectPort(item) {
    setPort(item.name);

    if (item.name) {
      setOrigin(ports.find((p) => p.name === item.name));
    } else {
      setOrigin(undefined);
    }

    hidePriceIfNewQuote();
  }

  function handleAddDelivery(e) {
    e.preventDefault();
    setDeliveries([
      ...deliveries,
      { address: '', lat: null, lng: null, coords: [], isNew: true },
    ]);
    deliveriesRef.current += 1;

    hidePriceIfNewQuote();
  }

  function handleSelectContainerSize(item) {
    setSize(item.size);
    hidePriceIfNewQuote();
  }

  function handleSelectContainerType(item) {
    setEquipmentType(item.name);
    hidePriceIfNewQuote();
  }

  function handleSelectMovementType(item) {
    setMovementType(item.name);
    hidePriceIfNewQuote();
  }

  function handleResetQuote() {
    setDistance(null);
    setOrigin(null);
    setDeliveries([
      { name: '', address: '', lat: null, lng: null, coords: [], isNew: true },
    ]);
    setPort('');
    setSize('');
    setEquipmentType('');
    setMovementType('');
    setAccessorials(accessorials.map((acc) => ({ ...acc, selected: false })));
    setSaving(false);
    setSuccess(false);
    setErrors({ deliveries: {} });
    setRequestCompleted(false);
    newFormRef.current = true;
  }

  async function handleSave() {
    const validationErrors = validate();

    if (
      !Object.values(validationErrors).every(
        (error) => Object.keys(error).length === 0,
      )
    ) {
      setErrors(validationErrors);
      if (deliveries.find((delivery) => delivery.isNew)) {
        setDeliveries(
          deliveries.map((delivery) => ({ ...delivery, isNew: false })),
        );
      }
      newFormRef.current = false;
      return;
    }

    const lastIndex = deliveries.length - 1;
    const lastDestination = deliveries[lastIndex];
    const request = {
      payables,
      receivables,
      distance,
      courier: false,
      origin: {
        name: '',
        address: origin.address,
      },
      destination: {
        name: '',
        address: lastDestination.address,
      },
      shipment_type: 'drayage',
      shipper: {
        _id: SessionStore.getUser()._id,
      },
      cost: price,
      movement_type: movementType,
      container_type: size,
    };

    try {
      setSaving(true);
      await createQuote(request);
      showToast('Quote Success', 'We retrieved the quote!', 'success');
      setSuccess(true);
    } catch (e) {
      setSuccess(false);
      console.error(e);
      showToast(
        'Get Quote Failed',
        'We were not able to get a quote.',
        'error',
      );
    }
    setSaving(false);
    setRequestCompleted(true);
  }

  function validateDeliveries(skipNew = false) {
    return {
      deliveries: deliveries.reduce((errorMap, delivery, i) => {
        if (!delivery.address && (!skipNew || (skipNew && !delivery.isNew))) {
          return {
            ...errorMap,
            [i]: { message: 'The address is required.' },
          };
        }
        return errorMap;
      }, {}),
    };
  }

  function validate() {
    return {
      ...validatePort(),
      ...validateDeliveries(),
      ...validateContainerSize(),
      ...validateContainerType(),
      ...validateMovementType(),
    };
  }

  function validatePort() {
    if (!port) {
      return {
        port: {
          message: 'The port is required.',
        },
      };
    }
    return { port: {} };
  }

  function validateContainerSize() {
    if (!size) {
      return {
        size: {
          message: 'The container size is required.',
        },
      };
    }
    return { size: {} };
  }

  function validateContainerType() {
    if (!equipmentType) {
      return {
        equipmentType: {
          message: 'The container type is required.',
        },
      };
    }
    return { equipmentType: {} };
  }

  function validateMovementType() {
    if (!movementType) {
      return {
        movementType: {
          message: 'The movement type is required',
        },
      };
    }
    return { movementType: {} };
  }

  function renderSelectedAccessorials() {
    return formatAccessorials(accessorials.filter((acc) => acc.selected));
  }

  const handleDeliveryAddressChange = (i) => () => {
    setDeliveries([
      ...deliveries.slice(0, i),
      {
        address: '',
        lat: null,
        lng: null,
        coors: [],
        isNew: false,
      },
      ...deliveries.slice(i + 1),
    ]);
    hidePriceIfNewQuote();
  };

  const handleDeliveryAddressSelect = (i) => (item) => {
    const { address, lat, lng, coords } = item;

    setDeliveries([
      ...deliveries.slice(0, i),
      {
        address,
        lat,
        lng,
        coords,
        isNew: false,
      },
      ...deliveries.slice(i + 1),
    ]);
    hidePriceIfNewQuote();
  };

  const handleRemoveDelivery = (i) => () => {
    setDeliveries([...deliveries.slice(0, i), ...deliveries.slice(i + 1)]);
    deliveriesRef.current -= 1;
    hidePriceIfNewQuote();
  };

  const handleAccessorialSelect = (slug) => () => {
    const index = accessorials.findIndex((acc) => acc.slug === slug);
    const accessorial = { ...accessorials[index] };
    accessorial.selected = !accessorial.selected;

    setAccessorials([
      ...accessorials.slice(0, index),
      accessorial,
      ...accessorials.slice(index + 1),
    ]);
    hidePriceIfNewQuote();
  };

  function handleToggleAccessorials() {
    setAccessorialsToggled(!accessorialsToggled);
  }

  function handleToggleDisclaimer() {
    setDisclaimerToggled(!disclaimerToggled);
  }

  async function getQuotePrice() {
    try {
      const result = await getPrice({
        accessorials: accessorials
          .filter((acc) => acc.selected)
          .map((acc) => acc.slug),
        origin,
        distance,
      });
      setPrice(result.totalCost);
      setMileageBand(result.mileageBand);
      setReceivables(result.receivables || []);
      setPayables(result.payables || []);
    } catch (e) {
      console.error(e);
    }
  }

  async function loadAccessorials() {
    try {
      const res = await getAccessorials({paginate: false});
      setAccessorials(
        res.entity.items
          .filter((acc) => availableAccessorials.includes(acc.slug))
          .map((acc) => ({
            ...acc,
            selected: false,
          })),
      );
    } catch (e) {
      console.error(e);
    }
  }

  return (
    <div>
      <img
        className="hidden object-cover w-full -mt-1 md:block"
        src="/img/drayage-bg.jpg"
        alt="Cargomatic Banner"
        style={{ height: 'calc(100vh - 64px)' }}
      />
      <div
        className="w-full h-full overflow-y-auto md:absolute md:left-0 md:top-16"
        style={{ maxHeight: 'calc(100vh - 220px)' }}
      >
        <div className="flex justify-center w-full h-full">
          <div className="w-full px-4 mt-10 md:w-2/3 lg:w-1/2 md:px-0">
            <Card
              header={() => (
                <div className="flex items-center space-x-2">
                  <span
                    className={getCircleButtonClass({
                      color: 'blue',
                      size: 'small',
                    })}
                  >
                    1
                  </span>
                  <h3 className="my-0 text-grey-darker">Location Details</h3>
                </div>
              )}
              grayFooter
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <CargomaticSelect
                  className=""
                  label="Pickup*"
                  info="Select a port"
                  items={ports}
                  valueKey="name"
                  textKey="label"
                  caption="Select a port"
                  showCaption
                  value={port}
                  onSelect={handleSelectPort}
                  error={errors.port}
                  hideError
                />
                <CargomaticInput
                  label="Delivery*"
                  type="location"
                  info="Delivery Address"
                  value={deliveries[0].address}
                  onChange={handleDeliveryAddressChange(0)}
                  onSelect={handleDeliveryAddressSelect(0)}
                  error={errors.deliveries[0]}
                  hideError
                />
              </div>
              {deliveries.length > 1 && (
                <div className="py-4 mt-4 -mx-4 sm:-mx-6 bg-gray-50">
                  {deliveries.slice(1).map((delivery, i) => (
                    <div
                      key={i}
                      className="flex items-end w-full px-6 mb-2 space-x-2 lg:w-2/3"
                    >
                      <div className="flex-1">
                        <CargomaticInput
                          className="flex-1"
                          label="Delivery"
                          type="location"
                          info="Delivery Address"
                          value={delivery.address}
                          onChange={handleDeliveryAddressChange(i + 1)}
                          onSelect={handleDeliveryAddressSelect(i + 1)}
                          error={errors.deliveries[i + 1]}
                          hideError
                        />
                      </div>
                      <DeleteButton onClick={handleRemoveDelivery(i + 1)} />
                    </div>
                  ))}
                </div>
              )}
              <a href="#" className="block mt-2" onClick={handleAddDelivery}>
                Add another delivery
              </a>
              <div className="mt-4 grid sm:grid-cols-3 gap-4">
                {containerSizes.length > 1 && (
                  <CargomaticSelect
                    className=""
                    label="Container Size*"
                    info="Select a container size"
                    items={containerSizes}
                    valueKey="size"
                    textKey="size"
                    showCaption
                    value={size}
                    onSelect={handleSelectContainerSize}
                    error={errors.size}
                    hideError
                  />
                )}
                {containerTypes.length > 1 && (
                  <CargomaticSelect
                    className=""
                    label="Container Type*"
                    info="Select a container type"
                    items={containerTypes}
                    valueKey="name"
                    textKey="name"
                    showCaption
                    value={equipmentType}
                    onSelect={handleSelectContainerType}
                    error={errors.equipmentType}
                    hideError
                  />
                )}
                <CargomaticSelect
                  className=""
                  label="Movement Type*"
                  info="Select a movement  type"
                  items={movementTypes}
                  valueKey="name"
                  textKey="name"
                  showCaption
                  value={movementType}
                  onSelect={handleSelectMovementType}
                  error={errors.movementType}
                  hideError
                />
              </div>
            </Card>
            <AccordionSection
              className="mt-8"
              title="Accessorials"
              stepNumber={2}
              onHeaderClick={handleToggleAccessorials}
              disabled={false}
              grayBody
              isActive={accessorialsToggled}
            >
              <div className="flex flex-wrap overflow-y-auto md:max-h-72">
                {accessorials.map((acc) => (
                  <div className="p-4" key={acc.slug}>
                    <AccessorialCard
                      key={acc.slug}
                      name={acc.name}
                      selected={acc.selected}
                      iconSlug={acc.icon_slug}
                      onSelect={handleAccessorialSelect(acc.slug)}
                      size="small"
                      canSelect
                    />
                  </div>
                ))}
              </div>
            </AccordionSection>
            <AccordionSection
              className="mt-8"
              title="Disclaimer"
              stepNumber={3}
              onHeaderClick={handleToggleDisclaimer}
              disabled={false}
              isActive={disclaimerToggled}
            >
              <p>
                Quotes provided by Cargomatic are indicative pricing based on
                information provided by the shipper. The quote cannot be
                considered firm until the shipment is booked and the exact
                details are known. Any changes to that shipment data or
                additional information provided will likely cause the quoted
                amount to be adjusted. For example, if an address is changed or
                postal code is replaced with a full address (which is required
                when booking a shipment), actual shipment rates will likely not
                match quoted amount due to difference in shipment data.
                Similarly, if the type of service, date of pick up and/or
                delivery, weight, pallet (count), box (count), insurance
                requirements, special or additional delivery instructions,
                accessorials, or other information required to confirm a
                shipment are added, removed or changed, the quoted amount is
                subject to change. If all information required to confirm a
                shipment is provided at the time the quote is requested and does
                not change prior to the confirmation of shipment, quotes are
                valid for a period of seven days.
              </p>
            </AccordionSection>
          </div>
        </div>
      </div>
      <footer className="absolute bottom-0 w-full py-4 bg-white shadow">
        <div className="flex items-start justify-center">
          <div className="flex flex-wrap items-start justify-between w-full px-4 lg:w-1/2 md:w-2/3 md:px-0">
            <div className="flex items-start justify-start mb-4 space-x-8">
              {distance && (
                <div>
                  <div className="text-lg font-medium">
                    {metersToMiles(distance).toFixed(2)} mile(s)
                  </div>
                  {mileageBand && (
                    <div className="text-sm text-gray-500">
                      {formatMileageBand(mileageBand)}
                    </div>
                  )}
                </div>
              )}
              {accessorials.filter((acc) => acc.selected).length > 0 && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Accessorials
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {renderSelectedAccessorials()}
                  </dd>
                </div>
              )}
            </div>
            {requestCompleted && success && (
              <div className="flex items-start space-x-4">
                <span className="text-2xl font-semibold text-green-cm">
                  {price ? formatMoney(price) : null}
                </span>
                <button
                  className="button button-green"
                  onClick={handleResetQuote}
                >
                  Start a New Quote
                </button>
              </div>
            )}
            {!requestCompleted && !success && (
              <div className="flex-grow-0 flex-shrink-0">
                <button
                  type="button"
                  className="flex-grow-0 flex-shrink-0 button button-green"
                  disabled={saving}
                  onClick={handleSave}
                >
                  {!saving && <span>Get Quote</span>}

                  {saving && <LoadingCircle text="Getting Quote..." />}
                </button>
              </div>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DrayageQuoteForm;
